import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
} from "@remix-run/react";

import isotipoImg from "./assets/academia-isotipo.png";
import logoUrl from "./assets/academia-logo-opt.png";
import heroSlide1Url from "./assets/illustrations/administrativa.png";
import { GlobalNavigationLoader } from "./components/GlobalNavigationLoader";
import { MarkdownAlternateTags } from "./components/MarkdownAlternateTags";
import { PlatformFavicon } from "./components/PlatformFavicon";
import { Toaster } from "./components/ui/toaster";
import { useNavigationTracker } from "./hooks/useNavigationTracker";
import css from "./index.css?url";
import CustomErrorBoundary from "./modules/common/ErrorBoundary/ErrorBoundary";
import { Providers } from "./modules/Global/Providers";
import { ThemeWrapper } from "./modules/Global/ThemeWrapper";
import { buildMeta } from "./utils/meta-helpers";

import type { LinksFunction, MetaFunction } from "@remix-run/node";

/** Fallback para rutas sin `meta` propio. Las hijas lo reemplazan por completo. */
export const meta: MetaFunction = () =>
  buildMeta({
    title: "AcademIA — Cursos de IA en español",
    description:
      "Aprendé IA en español, explicada para que la entienda cualquiera. Empezá hoy, gratis.",
  });

export const links: LinksFunction = () => {
  return [
    { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
    { rel: "icon", href: "/favicon.ico", sizes: "32x32" },
    { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
    { rel: "manifest", href: "/site.webmanifest" },
    { rel: "preload", as: "image", href: logoUrl },
    { rel: "preload", as: "image", href: heroSlide1Url },
    { rel: "stylesheet", href: css },
  ];
};

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="es-AR" suppressHydrationWarning>
      <head suppressHydrationWarning>
        <meta charSet="utf-8" />
        {/* `interactive-widget=resizes-content`: con el teclado abierto, Android achica el
            viewport de LAYOUT en vez de sólo el visual. Sin esto el reproductor —que es
            `fixed inset-0`— no se enteraba de que el teclado estaba tapando el composer del
            chat. iOS lo ignora y se resuelve por JS (`useKeyboardInset`). */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, interactive-widget=resizes-content"
        />
        <meta name="theme-color" content="#C0392B" />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function () {
                try {
                  var stored = JSON.parse(localStorage.getItem('theme-storage') || '{}');
                  var preference = stored && stored.state && stored.state.theme || 'system';
                  var dark = preference === 'dark' ||
                    (preference === 'system' && matchMedia('(prefers-color-scheme: dark)').matches);
                  document.documentElement.classList.toggle('dark', dark);
                  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
                } catch (_) {}
              })();
            `,
          }}
        />
        {/* Los OG/twitter salen de `meta` (abajo) y de cada ruta: acá estaban duplicados
            y con la URL de la imagen hardcodeada a prod, que no resuelve. */}
        <Meta />
        <Links />
        <MarkdownAlternateTags siteUrl={import.meta.env.VITE_APP_URL || ""} />
      </head>
      <body>
        {/* ═══ SPLASH SCREEN — visible from first byte, dismissed after load ═══ */}
        <div
          id="academia-splash"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: "16px",
            background: "rgb(var(--landing-cream, 245 240 228))",
            transition: "opacity 0.4s ease-out",
          }}
        >
          <img
            src={isotipoImg}
            alt=""
            width={72}
            height={72}
            style={{
              width: "72px",
              height: "72px",
              objectFit: "contain",
              animation: "splash-breathe 2s ease-in-out infinite",
            }}
          />
          <svg
            width="220"
            height="44"
            viewBox="0 0 220 44"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ animation: "splash-breathe 2s ease-in-out infinite" }}
          >
            <text
              x="110"
              y="34"
              textAnchor="middle"
              fontFamily="Georgia, serif"
              fontSize="32"
              fontWeight="600"
              letterSpacing="-0.5"
            >
              <tspan style={{ fill: "rgb(var(--landing-text, 31 26 21))" }}>Academ</tspan>
              <tspan style={{ fill: "rgb(var(--landing-red, 192 57 43))" }}>IA</tspan>
            </text>
          </svg>
          {/* splash bar below logo */}
          <div
            style={{
              width: "40px",
              height: "2px",
              background: "rgb(var(--landing-red, 192 57 43))",
              borderRadius: "1px",
              animation: "splash-bar 1.5s ease-in-out infinite",
            }}
          />
        </div>
        <style
          dangerouslySetInnerHTML={{
            __html: `
              @keyframes splash-breathe {
                0%, 100% { opacity: 1; transform: scale(1); }
                50% { opacity: 0.7; transform: scale(1.02); }
              }
              @keyframes splash-bar {
                0%, 100% { width: 40px; opacity: 0.3; }
                50% { width: 80px; opacity: 1; }
              }
            `,
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              // Dismiss splash when fonts + critical images are loaded
              (function() {
                var splash = document.getElementById('academia-splash');
                if (!splash) return;

                // Preload critical images
                var imgs = [${JSON.stringify(logoUrl)}, ${JSON.stringify(heroSlide1Url)}];
                var imgPromises = imgs.map(function(src) {
                  return new Promise(function(resolve) {
                    var img = new Image();
                    img.onload = resolve;
                    img.onerror = resolve; // don't block on error
                    img.src = src;
                  });
                });

                function dismissSplash() {
                  if (splash.dataset.dismissed) return;
                  splash.dataset.dismissed = '1';
                  splash.style.opacity = '0';
                  splash.style.pointerEvents = 'none';
                  setTimeout(function() { splash.style.display = 'none'; }, 400);
                }

                // Wait for fonts + images
                Promise.all([
                  document.fonts ? document.fonts.ready : Promise.resolve(),
                ].concat(imgPromises)).then(dismissSplash);

                // Safety timeout — never show splash longer than 3 seconds
                setTimeout(dismissSplash, 3000);
              })();
            `,
          }}
        />
        {children}
        <script
          type="text/javascript"
          src={
            import.meta.env.VITE_PLAYERJS_CDN_URL ||
            "//assets.mediadelivery.net/playerjs/playerjs-latest.min.js"
          }
          defer
        />
        <ScrollRestoration />
        <Scripts />
        <Toaster />
      </body>
    </html>
  );
}

export default function Root() {
  useNavigationTracker();

  return (
    <Providers>
      <ThemeWrapper>
        <PlatformFavicon />
        <GlobalNavigationLoader />
        <Outlet />
      </ThemeWrapper>
    </Providers>
  );
}

export function HydrateFallback() {
  return <div />;
}
export function ErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    return <CustomErrorBoundary stack={error.data} message={error.statusText} />;
  } else if (error instanceof Error) {
    return <CustomErrorBoundary stack={error.stack} message={error.message} />;
  } else {
    return <CustomErrorBoundary />;
  }
}
